import Logo from "assets/images/logo.png";
import FavIcon from "assets/images/favicon.png";
import { setConfig, setSelectedPhoneNumber } from "appredux/configSlice";
import { Avatar, Badge, Layout, Menu } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router";
import store from "store";
import {
  accessiblePhoneNumbersSelector,
  conversationTypeSelector,
  crmIsoSettingSelector, // [crm-iso]
  getAvailableConversationTypes,
  inboxTicketSelector,
  isActiveISOSettingSelector, // [crm-iso]
  isAdministratorSelector,
  isAgentCanTakeOverConversationSelector,
  isAssignModeSelector,
  isMenuCollapseSelector,
  isMobileViewSelector,
  isSupervisorSelector,
  loadedInitSelector,
  permissionNamesSelector,
  phoneNumbersSelector,
  selectedCompanySelector,
  selectedPhoneNumberSelector,
  userSelector,
} from "appredux/selectors";
import { isAffectedProviderConversation } from "components/apps/settings/CrmIsoSetting/helper"; // [crm-iso]
import {
  setChat,
  setConversationType,
  setLastMessageId,
} from "appredux/chatSlice";
import { AppIcon } from "components/UI/icon";
import ConversationBadge from "./ConversationBadge";
import { useConversations } from "queries/conversation";
import Scrollbars from "react-custom-scrollbars-2";
import { ExclamationCircleFilled, MessageOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import MenuSiderLoading from "./MenuSiderLoading";
import { find, includes, isEmpty, split } from "lodash-es";
import useRemainingBalance from "hooks/useRemainingBalance";
import { useGetInboxTicketing } from "queries/ticketing";
import { conversationStatuses } from "utils/static";

const SIDER_WIDTH = 256;

const Sider = () => {
  const { t } = useTranslation();
  const location = useLocation();
  // const scrollBarRef = useRef();
  const dispatch = useDispatch();
  // const selectedKeysFromStore = store.get("app.menu.selectedKeys");
  // const openedKeysFromStore = store.get("app.menu.openedKeys");
  // const loadedInit = useSelector(loadedInitSelector);
  // const loadedInit = useSelector(loadedInitSelector);

  // const [openedKeys, setOpenedKeys] = useState(openedKeysFromStore || []);
  const [openedKeys, setOpenedKeys] = useState([]);
  const isMenuCollapsed = useSelector(isMenuCollapseSelector);
  const phoneNumbers = useSelector(phoneNumbersSelector);
  const selectedPhoneNumber = useSelector(selectedPhoneNumberSelector);
  const isAssignMode = useSelector(isAssignModeSelector);
  const isMobileView = useSelector(isMobileViewSelector);
  const conversation_type = useSelector(conversationTypeSelector);
  // const loadedInit = useSelector(loadedInitSelector);
  const isAdministrator = useSelector(isAdministratorSelector);
  const isSupervisor = useSelector(isSupervisorSelector);
  const isAgentCanTakeOver = useSelector(
    isAgentCanTakeOverConversationSelector,
  );
  const company = useSelector(selectedCompanySelector);
  const user = useSelector(userSelector);
  const inboxTicket = useSelector(inboxTicketSelector);
  const loadedInit = useSelector(loadedInitSelector);
  const crmIsoSetting = useSelector(crmIsoSettingSelector); // [crm-iso]
  const isActiveISO = useSelector(isActiveISOSettingSelector); // [crm-iso]
  const { isShowWarnBalanceNotification, remainingBalance } =
    useRemainingBalance();

  const { data: conversations } = useConversations(
    {
      inbox_filter: isAssignMode ? conversation_type : null,
    },
    {
      enabled: loadedInit,
    },
  );

  const { data: inboxTicketing } = useGetInboxTicketing(
    {},
    {
      active_only: 1,
      include_count: 1,
    },
  );

  useEffect(() => {
    if (
      conversations?.last_message_id &&
      !includes(location.pathname, "/conversations")
    ) {
      dispatch(setLastMessageId(conversations?.last_message_id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations?.last_message_id, dispatch]);

  // const [selectedKeys, setSelectedKeys] = useState(selectedKeysFromStore || []);
  const permissions = useSelector(permissionNamesSelector);
  const toggleMenuColllapsedHandler = useCallback(
    (collapsed) => {
      store.set("app.menu.isMenuCollapsed", collapsed);
      dispatch(setConfig({ isMenuCollapsed: collapsed }));
    },
    [dispatch],
  );
  // Channel active yang boleh diakses (lihat getAccessiblePhoneNumbers)
  const phoneNumbersData = useSelector(accessiblePhoneNumbersSelector);

  const menuSettings = useMemo(() => {
    return isMobileView
      ? {
          width: SIDER_WIDTH,
          collapsible: false,
          collapsed: false,
          collapsedWidth: 0,
          onCollapse: toggleMenuColllapsedHandler,
        }
      : {
          width: SIDER_WIDTH,
          collapsible: true,
          collapsed: isMenuCollapsed,
          onCollapse: toggleMenuColllapsedHandler,
          breakpoint: "lg",
        };
  }, [isMenuCollapsed, isMobileView, toggleMenuColllapsedHandler]);

  const generateItem = useCallback(
    ({ item, isPhoneNumber }) => {
      if (item.url) {
        const url = item.url;
        let linkComponent = <Link to={url}>{item.name}</Link>;
        if (item.group === "chats") {
          let filterConversations = conversations?.data?.filter(
            (row) => row.phone_number_id === item.id,
          );

          // Samakan dengan list: jangan hitung percakapan yang CLOSE di badge.
          filterConversations = filterConversations?.filter(
            (row) => row.status_id !== conversationStatuses.CLOSE,
          );

          // Tab aktif (mis. "all" dari channel tempat user supervisor) belum
          // tentu tersedia di channel ini sesuai role user di channel tsb. Saat
          // dibuka, channel ini akan memakai tab "my_inbox"
          // (useSyncConversationType), jadi badge ikut hitung milik user saja.
          const availableTypes = getAvailableConversationTypes({
            isSupervisor: item.roles?.includes(1),
            isAdministrator,
            isAgentCanTakeOver,
          });
          if (
            isAssignMode &&
            conversation_type &&
            !includes(availableTypes, conversation_type)
          ) {
            filterConversations = filterConversations?.filter(
              (row) => row.current_agent_id === user?.id,
            );
          }

          // [crm-iso] START — saat fitur aktif untuk agen ini, badge hanya hitung
          // percakapan dari provider yang di-setting (affected_providers).
          if (isActiveISO) {
            filterConversations = filterConversations?.filter((row) =>
              isAffectedProviderConversation(
                row,
                crmIsoSetting?.affected_providers,
              ),
            );
          }
          // [crm-iso] END

          linkComponent = (
            <Link to={url}>
              <div className="flex flex-row items-center justify-between pr-[2px]  w-full">
                <span className="flex-1 min-w-0 truncate">{item.name}</span>
                <ConversationBadge
                  total={filterConversations?.length}
                  className="flex-shrink-0 ml-2"
                />
              </div>
            </Link>
          );
        }

        if (item.group === "ticketing") {
          const inbox = inboxTicketing?.find((row) => row.id === item.id);
          linkComponent = (
            <Link to={url}>
              <div className="flex flex-row items-center justify-between pr-[2px] w-full">
                <span className="flex-1 min-w-0 truncate">{item.name}</span>
                <Badge count={inbox?.count} className="flex-shrink-0 ml-2" />
              </div>
            </Link>
          );
        }

        return {
          icon: isPhoneNumber ? (
            !item.icon_url ? (
              <MessageOutlined
                className={`text-[24px] ${isMenuCollapsed ? "mt-2" : ""}`}
              />
            ) : (
              <Avatar
                size={24}
                src={
                  item.icon_url
                    ? `${item.icon_url}?lastmod=${new Date().getTime()}`
                    : null
                }
                className={`bg-white ${isMenuCollapsed ? "mt-2" : ""}`}
              />
            )
          ) : (
            <AppIcon
              className={`text-[24px] ${isMenuCollapsed ? "mt-2" : ""}`}
              name={item.group}
            />
          ),
          key: item.key,
          id: item.id,
          label: linkComponent,
          disabled: item.disabled,
          url: item.url,
          children: item.children,
        };
      }

      return {
        icon: (
          <AppIcon
            className={`text-[24px] ${isMenuCollapsed ? "mt-2" : ""}`}
            name={item.group}
          />
        ),
        key: item.key,
        id: item.id,
        label: item.name,
        disabled: item.disabled,
        children: item.children,
      };
    },
    [
      conversations?.data,
      isMenuCollapsed,
      inboxTicketing,
      isAssignMode,
      conversation_type,
      isAdministrator,
      isAgentCanTakeOver,
      user?.id,
      isActiveISO,
      crmIsoSetting,
    ], // [crm-iso] deps: isActiveISO, crmIsoSetting
  );

  const menus = useMemo(() => {
    let items = [];
    if (permissions?.includes("contacts")) {
      items.push(
        generateItem({
          item: {
            name: t("contacts"),
            key: "menu-contact",
            id: 2,
            url: "/contacts",
            group: "contact",
          },
        }),
      );
    }
    if (
      permissions?.includes("tasks") &&
      permissions?.includes("task_list") &&
      (company?.company_id === 1 ||
        company?.company_id === 107014 ||
        company?.company_id === 131158)
    ) {
      items.push(
        generateItem({
          item: {
            name: "Tasks",
            key: "lead-and-task-management",
            id: 4,
            url: "/lead-and-task-management",
            group: "lead-and-task-management",
          },
        }),
      );
    }

    if (
      permissions?.includes("wa_blast") &&
      permissions?.includes("wa_blast_list")
    ) {
      items.push(
        generateItem({
          item: {
            name: t("blast-message"),
            key: "blast-message",
            id: 3,
            url: "/blast-message",
            group: "blast",
          },
        }),
      );
    }
    if (permissions?.includes("quality_assurance")) {
      items.push(
        generateItem({
          item: {
            name: "Quality Assurance",
            key: "quality-assurance",
            id: 8,
            url: "/quality-assurance-menu",
            group: "quality-assurance",
          },
        }),
      );
    }

    if (
      permissions?.includes("meeting_schedule") &&
      (company?.company_id === 1 ||
        company?.company_id === 107014 ||
        company?.company_id === 131158) //hide sementara https://kledo.slack.com/archives/C06KNDRCZPG/p1748923380207779?thread_ts=1748572018.281699&cid=C06KNDRCZPG
    ) {
      items.push(
        generateItem({
          item: {
            name: t("meeting_management"),
            key: "meeting_management",
            id: 7,
            url: "/meeting-management",
            group: "meeting_management",
          },
        }),
      );
    }
    if (permissions?.includes("reports")) {
      items.push(
        generateItem({
          item: {
            name: t("reports"),
            key: "menu-reports",
            id: 5,
            url: "/reports",
            group: "reports",
          },
        }),
      );
    }

    // if (
    //   permissions?.some(
    //     (permission) =>
    //       permission.name === "ticketing" &&
    //       permission.child &&
    //       permission.child.some((child) => child.name === "ticketing_list")
    //   )
    // ) {
    // [crm-iso] Agen terpengaruh tidak melihat menu Ticketing.
    // TEARDOWN: hapus kondisi `if (!isActiveISO)` (biarkan items.push-nya) + isActiveISO dari deps.
    if (!isActiveISO) {
      items.push(
        generateItem({
          item: {
            name: "Ticketing",
            key: "ticketing",
            id: 9,
            // url: "/ticketing",
            group: "ticketing",
            children: inboxTicket?.map((item) => {
              return generateItem({
                item: {
                  name: item.name,
                  key: `ticketing-${item.id}`,
                  id: item.id,
                  url: `/ticketing/${item.id}`,
                  group: "ticketing",
                },
              });
            }),
          },
        }),
      );
    } // [crm-iso] END if (!isActiveISO)
    // }

    if (permissions?.some((perm) => perm.startsWith("setting_"))) {
      items.push(
        generateItem({
          item: {
            name: t("settings"),
            key: "menu-pengaturan",
            id: 1,
            url: "/settings",
            group: "settings",
          },
        }),
      );
    }
    items.push(
      generateItem({
        item: {
          name: t("faqs"),
          key: "menu-faqs",
          id: 6,
          url: "/faqs",
          group: "faqs",
        },
      }),
    );

    return items;
  }, [t, generateItem, permissions, company, inboxTicket, isActiveISO]); // [crm-iso] deps: isActiveISO

  const renderMenuItems = useMemo(() => {
    const phoneNumberGroup = {
      key: "phoneNumber",
      id: "phoneNumber",
      label: t("sider.channel"),
      type: "group",
      children: phoneNumbersData.map((item) => {
        return generateItem({
          item: {
            ...item,
            key: `number-${item.id}`,
            group: "chats",
            url: `/conversations/${item.id}/list`,
          },
          isPhoneNumber: true,
        });
      }),
    };
    const divider = {
      type: "divider",
    };

    const settingMenuGroup = {
      key: "settingMenu",
      id: "settingMenu",
      label: "Applications",
      type: "group",
      //   className: `h-[68px] bottom-14 fixed ${
      //     isMenuCollapsed ? "w-[80px]" : "w-[256px]"
      //   }`,
      // className: "absolute bottom-0 w-full",
      children: menus,
    };
    let result = phoneNumbersData.length > 0 ? [phoneNumberGroup, divider] : [];
    if (menus.length > 0) result.push(settingMenuGroup);

    // return [phoneNumberGroup, settingMenuGroup];
    return result;
  }, [t, generateItem, menus, phoneNumbersData]);

  // Key menu yang benar-benar bisa dinavigasi (punya url). Dipakai membatasi
  // highlight optimistic supaya klik parent submenu yang tidak punya url
  // (mis. "Ticketing") tidak menghapus highlight yang sedang aktif.
  const navigableKeys = useMemo(() => {
    const keys = new Set(["menu-help"]);
    const walk = (items) => {
      items.forEach((item) => {
        if (item?.url) {
          keys.add(item.key);
        }
        if (item?.children) {
          walk(item.children);
        }
      });
    };
    walk(renderMenuItems);
    return keys;
  }, [renderMenuItems]);

  // react-router v7 membungkus perubahan lokasi dalam startTransition, jadi React
  // baru commit setelah render halaman tujuan siap. Terukur 18ms s/d 228ms,
  // tergantung chunk & data halaman itu sudah ter-cache atau belum. Tanpa key
  // optimistic ini, selama jeda tersebut menu LAMA masih tersorot penuh sehingga
  // klik terasa tidak direspons lalu highlight "meloncat" belakangan.
  const [optimisticKey, setOptimisticKey] = useState(null);

  const clickMenuHandler = useCallback(
    (e) => {
      // Sorot menu yang diklik seketika, jangan tunggu commit transisi router.
      if (navigableKeys.has(e.key)) {
        setOptimisticKey(e.key);
      }

      // Jika dia nomor chating
      const clickedPhoneNumber = includes(e.key, "number-")
        ? phoneNumbers.find(
            (row) => `${row.id}` === `${split(e.key, "-")[1]}`,
          )
        : null;
      const isSwitchPhoneNumber =
        !isEmpty(clickedPhoneNumber) &&
        clickedPhoneNumber.id !== selectedPhoneNumber?.id;

      // Saat pindah channel, isSupervisor di sini masih milik channel lama —
      // tab inbox untuk channel baru disesuaikan oleh useSyncConversationType.
      if (isAssignMode && !isSwitchPhoneNumber) {
        if (!isSupervisor && !isAdministrator && !isAgentCanTakeOver) {
          dispatch(setConversationType("my_inbox"));
        }
        if (conversation_type === null) {
          if (isSupervisor || isAdministrator) {
            dispatch(setConversationType("all"));
          } else {
            dispatch(setConversationType("my_inbox"));
          }
        }
      }
      store.set("app.menu.selectedKeys", [e.key]);
      if (!isEmpty(clickedPhoneNumber)) {
        dispatch(setSelectedPhoneNumber(clickedPhoneNumber));
        dispatch(setChat({ conversation: null, messages: [] }));
      }
    },
    [
      dispatch,
      navigableKeys,
      phoneNumbers,
      selectedPhoneNumber,
      isAdministrator,
      isSupervisor,
      isAgentCanTakeOver,
      isAssignMode,
      conversation_type,
    ],
  );

  // const openChangeHandler = useCallback((openedKeys) => {
  //   setOpenedKeys(openedKeys);
  // }, []);

  const pathnameSelectedKey = useMemo(() => {
    return findKeyByPathname(renderMenuItems, location.pathname);
  }, [location.pathname, renderMenuItems]);

  // Begitu pathname menyusul, lepas highlight optimistic supaya sumber kebenaran
  // kembali ke URL (navigasi lewat breadcrumb, tombol back, atau redirect).
  useEffect(() => {
    setOptimisticKey(null);
  }, [pathnameSelectedKey]);

  const selectedKeys = optimisticKey ?? pathnameSelectedKey;

  const findParentKeyByPathname = useCallback((items, pathname) => {
    for (const item of items) {
      if (item.children && Array.isArray(item.children)) {
        const childMatch = item.children.some((child) => {
          if (child.url && pathname.includes(child.url)) {
            return true;
          }
          return false;
        });

        if (childMatch) {
          return item.key;
        }

        const nestedParent = findParentKeyByPathname(item.children, pathname);
        if (nestedParent) {
          return nestedParent;
        }
      }
    }
    return null;
  }, []);

  useEffect(() => {
    const parentKey = findParentKeyByPathname(
      renderMenuItems,
      location.pathname,
    );
    if (parentKey) {
      setOpenedKeys((prevKeys) => {
        if (!prevKeys.includes(parentKey)) {
          return [parentKey];
        }
        return prevKeys;
      });
    }
  }, [location.pathname, renderMenuItems, findParentKeyByPathname]);

  const openChangeHandler = useCallback((keys) => {
    setOpenedKeys(keys);
  }, []);

  return (
    <Layout.Sider
      className="h-[calc(100%-0px)] left-0 bottom-0 top-0 p-0 fixed [&>.ant-layout-sider-children]:flex [&>.ant-layout-sider-children]:flex-col"
      {...menuSettings}
    >
      <div className="flex h-[80px] shrink-0 justify-center items-center ">
        <Link to="/">
          {isMenuCollapsed ? (
            <img className="h-[30px]" src={FavIcon} alt="Logo" />
          ) : (
            <img className="h-[30px]" src={Logo} alt="Logo" />
          )}
        </Link>
      </div>
      {isShowWarnBalanceNotification && !isMenuCollapsed && (
        <div className="text-sm bg-yellow-200 flex h-[80px] shrink-0 justify-center items-center px-4 gap-x-3">
          <ExclamationCircleFilled className="text-yellow-500" />
          <div>
            {t("billing.remaining_balance_notification", {
              balance: remainingBalance,
            })}
          </div>
        </div>
      )}
      {/* Mengisi sisa tinggi di antara logo/banner dan Help, jadi tidak perlu
          hitung px manual saat banner saldo muncul/hilang. */}
      <div className="flex-1 min-h-0">
        {!loadedInit ? (
          <MenuSiderLoading isMenuCollapsed={isMenuCollapsed} />
        ) : (
          <Scrollbars autoHide>
            <Menu
              mode="inline"
              selectedKeys={[selectedKeys]}
              openKeys={openedKeys}
              onOpenChange={openChangeHandler}
              onClick={clickMenuHandler}
              items={renderMenuItems}
              theme="dark"
            />
          </Scrollbars>
        )}
      </div>

      {/* mb-[48px]: ruang untuk tombol collapse antd (padding bawaannya ketimpa p-0). */}
      <div
        className={`w-full shrink-0 border-t border-gray-800 pt-2 bg-[#001529] ${
          isMobileView ? "" : "mb-[48px]"
        }`}
      >
        {!loadedInit ? (
          <div className="m-1 px-6 py-2 flex flex-row items-center gap-x-[10px]  rounded-lg animate-pulse cursor-pointer">
            <div className="size-6 rounded-full bg-slate-700 " />
            {!isMenuCollapsed && <div className="w-36 h-4 bg-slate-700" />}
          </div>
        ) : (
          <Menu
            mode="inline"
            selectedKeys={[selectedKeys]}
            onClick={clickMenuHandler}
            items={[
              generateItem({
                item: {
                  name: "Help",
                  key: "menu-help",
                  id: 7,
                  url: "/help",
                  group: "help",
                },
              }),
            ]}
            theme="dark"
          />
        )}
      </div>
    </Layout.Sider>
  );
};

// Cocokkan url menu dengan pathname pada batas segmen path, bukan substring
// mentah. `pathname.includes(url)` bikin /ticketing/15 ikut cocok dengan url
// /ticketing/1, sehingga inbox yang tersorot bisa keliru.
const isPathnameUnderUrl = (pathname, url) =>
  !!url && (pathname === url || pathname.startsWith(`${url}/`));

const findKeyByPathname = (items, pathname) => {
  let foundItem = null;
  // Cari item yang sesuai dengan pathname
  if (new RegExp("^/conversations/\\d").test(pathname)) {
    foundItem = find(items, (item) => {
      const replaceUrl = item?.url?.replace("list", "(list|search|create)");
      return new RegExp(`^${replaceUrl}$`).test(pathname);
    });
  } else {
    foundItem = find(items, (item) => isPathnameUnderUrl(pathname, item.url));
  }

  // console.log(
  //   new RegExp(
  //     `^${"/conversations/3/list".replace("list", "(list|search)")}`
  //   ).test(pathname)
  // );
  if (foundItem) {
    return foundItem.key; // Jika ditemukan, kembalikan key dari item ini
  }

  // Jika tidak ditemukan, cari di children (jika ada)
  for (const item of items) {
    if (item.children) {
      const childKey = findKeyByPathname(item.children, pathname);
      if (childKey) {
        return childKey; // Jika ditemukan di children, kembalikan key tersebut
      }
    }
  }

  if (pathname.startsWith("/help")) {
    return "menu-help";
  }

  return null; // Jika tidak ditemukan, kembalikan null
};

export default Sider;
